import * as React from 'react';
import { useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Unstable_Grid2 as Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';
import { toast } from '@/components/core/v2/toaster';

import type { Community } from '@/types/v2/community';
import type { UnitType } from '@/types/v2/unit-type';
import axios from '@/lib/v2/axios';
import { useUser } from '@/hooks/v2/use-user';

const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Mountain Time – Arizona (no DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
];

const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

const schema = zod.object({
  name: zod.string().min(1, 'A community name is required'),
  email: zod.string().email('Invalid email address').optional().or(zod.literal('')),
  phone: zod
    .string()
    .regex(/^\+?[1-9]\d{1,14}$/, 'Invalid phone number')
    .optional()
    .or(zod.literal('')),
  address: zod.string().optional(),
  address2: zod.string().optional(),
  city: zod.string().optional(),
  state: zod.string().optional(),
  zip_code: zod
    .string()
    .regex(/^\d{5}$/, 'Invalid zip code')
    .optional()
    .or(zod.literal('')),
  timezone: zod.string().min(1, 'A timezone is required'),
});
type Values = zod.infer<typeof schema>;

function Section({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
    >
      <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ p: 3 }}>{children}</Box>
    </Paper>
  );
}

export function CommunitySettingsPage(): React.JSX.Element {
  const { community, setCommunity } = useUser();
  const [error, setError] = useState<string | null>(null);

  const defaultValues = React.useMemo<Values>(() => ({
    name: community?.name || '',
    email: community?.email || '',
    phone: community?.phone || '',
    address: community?.address || '',
    address2: community?.address2 || '',
    city: community?.city || '',
    state: community?.state || '',
    zip_code: community?.zip_code || '',
    timezone: community?.timezone || 'America/Chicago',
  }), [community]);

  const { control, handleSubmit, reset, formState: { errors, isDirty } } = useForm<Values>({
    defaultValues,
    resolver: zodResolver(schema),
  });

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const updateMutation = useMutation({
    mutationFn: async (data: Values) => {
      await axios.get('/sanctum/csrf-cookie');
      const response = await axios.put(`/api/v2/communities/${community?.id}`, data);
      return response.data;
    },
  });

  const onSubmit = useCallback(
    async (data: Values) => {
      setError(null);
      try {
        const updated: Community = await updateMutation.mutateAsync(data);
        // The PUT response is a bare community model with no `roles` key, so
        // replacing the context wholesale wiped community.roles and the nav lost
        // the Management section until a page reload. Merge, don't replace.
        setCommunity({ ...community!, ...updated }, false);
        reset({
          name: updated.name || '',
          email: updated.email || '',
          phone: updated.phone || '',
          address: updated.address || '',
          address2: updated.address2 || '',
          city: updated.city || '',
          state: updated.state || '',
          zip_code: updated.zip_code || '',
          timezone: updated.timezone || 'America/Chicago',
        });
        toast.success('Community updated successfully');
      } catch (err: any) {
        setError(err?.response?.data?.message || 'An error occurred while saving');
      }
    },
    // `community` is a dependency now that the submit merges into it — without
    // it a later submit could merge into a stale community object.
    [community, updateMutation, setCommunity, reset]
  );

  return (
    <Stack spacing={3}>
      {error ? <Alert severity="error">{error}</Alert> : null}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3}>
          <Section title="Basic Information">
            <Grid container spacing={3}>
              <Grid xs={12} md={6}>
                <Controller
                  control={control}
                  name="name"
                  render={({ field }) => (
                    <FormControl fullWidth error={Boolean(errors.name)}>
                      <InputLabel required>Community Name</InputLabel>
                      <OutlinedInput {...field} disabled={updateMutation.isPending} />
                      {errors.name ? <FormHelperText>{errors.name.message}</FormHelperText> : null}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <Controller
                  control={control}
                  name="email"
                  render={({ field }) => (
                    <FormControl fullWidth error={Boolean(errors.email)}>
                      <InputLabel>Email</InputLabel>
                      <OutlinedInput {...field} disabled={updateMutation.isPending} />
                      {errors.email ? <FormHelperText>{errors.email.message}</FormHelperText> : null}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <Controller
                  control={control}
                  name="phone"
                  render={({ field }) => (
                    <FormControl fullWidth error={Boolean(errors.phone)}>
                      <InputLabel>Phone</InputLabel>
                      <OutlinedInput {...field} disabled={updateMutation.isPending} />
                      {errors.phone ? <FormHelperText>{errors.phone.message}</FormHelperText> : null}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <Controller
                  control={control}
                  name="timezone"
                  render={({ field }) => (
                    <FormControl fullWidth error={Boolean(errors.timezone)}>
                      <InputLabel required>Timezone</InputLabel>
                      <Select {...field} disabled={updateMutation.isPending} label="Timezone">
                        {US_TIMEZONES.map((tz) => (
                          <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>Used to validate amenity availability hours.</FormHelperText>
                      {errors.timezone ? <FormHelperText error>{errors.timezone.message}</FormHelperText> : null}
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="Location">
            <Grid container spacing={3}>
              <Grid xs={12} md={8}>
                <Controller
                  control={control}
                  name="address"
                  render={({ field }) => (
                    <FormControl fullWidth error={Boolean(errors.address)}>
                      <InputLabel>Address</InputLabel>
                      <OutlinedInput {...field} disabled={updateMutation.isPending} />
                      {errors.address ? <FormHelperText>{errors.address.message}</FormHelperText> : null}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid xs={12} md={4}>
                <Controller
                  control={control}
                  name="address2"
                  render={({ field }) => (
                    <FormControl fullWidth error={Boolean(errors.address2)}>
                      <InputLabel>Address 2</InputLabel>
                      <OutlinedInput {...field} disabled={updateMutation.isPending} />
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid xs={12} md={5}>
                <Controller
                  control={control}
                  name="city"
                  render={({ field }) => (
                    <FormControl fullWidth error={Boolean(errors.city)}>
                      <InputLabel>City</InputLabel>
                      <OutlinedInput {...field} disabled={updateMutation.isPending} />
                      {errors.city ? <FormHelperText>{errors.city.message}</FormHelperText> : null}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid xs={12} md={4}>
                <Controller
                  control={control}
                  name="state"
                  render={({ field }) => (
                    <FormControl fullWidth error={Boolean(errors.state)}>
                      <InputLabel>State</InputLabel>
                      <Select {...field} disabled={updateMutation.isPending} label="State">
                        {US_STATES.map((s) => (
                          <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                        ))}
                      </Select>
                      {errors.state ? <FormHelperText>{errors.state.message}</FormHelperText> : null}
                    </FormControl>
                  )}
                />
              </Grid>
              <Grid xs={12} md={3}>
                <Controller
                  control={control}
                  name="zip_code"
                  render={({ field }) => (
                    <FormControl fullWidth error={Boolean(errors.zip_code)}>
                      <InputLabel>Zip Code</InputLabel>
                      <OutlinedInput {...field} disabled={updateMutation.isPending} />
                      {errors.zip_code ? <FormHelperText>{errors.zip_code.message}</FormHelperText> : null}
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </Section>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <LoadingButton
              disabled={!isDirty}
              loading={updateMutation.isPending}
              type="submit"
              variant="contained"
            >
              Save Changes
            </LoadingButton>
          </Box>
        </Stack>
      </form>
    </Stack>
  );
}
